export type VideoEmbed = { kind: 'iframe'; src: string } | { kind: 'file'; src: string };

const YOUTUBE_ID = /^[\w-]{11}$/;
const VIMEO_ID = /^\d+$/;
// The privacy hash an unlisted Vimeo video needs in its embed URL.
const VIMEO_HASH = /^[\da-f]+$/i;
const VIDEO_FILE = /\.(mp4|webm|mov|m4v)$/i;

function youtubeId(host: string, url: URL, segments: string[]): string | undefined {
  if (host === 'youtu.be') return segments[0];
  if (host !== 'youtube.com' && host !== 'youtube-nocookie.com') return undefined;
  if (segments[0] === 'watch') return url.searchParams.get('v') ?? undefined;

  return segments[0] === 'embed' || segments[0] === 'shorts' ? segments[1] : undefined;
}

function vimeoEmbedSrc(host: string, url: URL, segments: string[]): string | undefined {
  let id: string | undefined;
  let hash: string | null | undefined;
  if (host === 'vimeo.com') {
    [id, hash] = segments;
  } else if (host === 'player.vimeo.com' && segments[0] === 'video') {
    id = segments[1];
    hash = url.searchParams.get('h');
  }
  if (!id || !VIMEO_ID.test(id)) return undefined;

  const privacy = hash && VIMEO_HASH.test(hash) ? `&h=${hash}` : '';
  return `https://player.vimeo.com/video/${id}?autoplay=1${privacy}`;
}

/**
 * How a video URL plays on the page, or null to leave it as a link. Embeds are rebuilt from the
 * parsed id rather than passed through, so a stored value can never choose an iframe's origin.
 */
export function resolveVideoEmbed(value: string): VideoEmbed | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const segments = url.pathname.split('/').filter(Boolean);

  const youtube = youtubeId(host, url, segments);
  if (youtube && YOUTUBE_ID.test(youtube)) {
    return {
      kind: 'iframe',
      src: `https://www.youtube-nocookie.com/embed/${youtube}?autoplay=1&rel=0`,
    };
  }

  const vimeo = vimeoEmbedSrc(host, url, segments);
  if (vimeo) return { kind: 'iframe', src: vimeo };

  return VIDEO_FILE.test(url.pathname) ? { kind: 'file', src: url.href } : null;
}
